import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from './Icon';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastOptions {
  tone?: ToastTone;
  /** Keep until dismissed (errors that need action). Default: auto-dismiss after `durationMs`. */
  persistent?: boolean;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  persistent: boolean;
  durationMs: number;
}

export type ToastPlacement = 'bottom' | 'top';

interface ToastApi {
  show: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
  /** Where toasts float. `bottom` everywhere unless a screen asks otherwise (see `useToastPlacement`). */
  setPlacement: (placement: ToastPlacement) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<ToastTone, string> = {
  info: 'bg-ink text-white',
  success: 'bg-success text-white',
  error: 'bg-danger text-white',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [placement, setPlacement] = useState<ToastPlacement>('bottom');
  const nextId = useRef(1);

  const dismiss = useCallback(
    (id: number) => setItems((list) => list.filter((t) => t.id !== id)),
    [],
  );
  const show = useCallback((message: string, options: ToastOptions = {}) => {
    const id = nextId.current++;
    const tone = options.tone ?? 'info';
    setItems((list) => [
      ...list,
      {
        id,
        message,
        tone,
        persistent: options.persistent ?? false,
        durationMs: options.durationMs ?? 3000,
      },
    ]);
    return id;
  }, []);

  const api = useMemo(() => ({ show, dismiss, setPlacement }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="region"
        aria-label="Notifications"
        className={cn(
          'pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4',
          placement === 'top' ? 'top-20' : 'bottom-20',
        )}
      >
        {items.map((item) => (
          <ToastView key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (item.persistent) return;
    const timer = setTimeout(() => onDismiss(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item, onDismiss]);

  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex max-w-md items-center gap-3 rounded-control px-4 py-3 text-sm font-medium shadow-dialog',
        // A passing toast never blocks a tap on what is under it (the sticky action bar sits where it
        // floats); only a persistent one, which has a button, takes the pointer.
        item.persistent ? 'pointer-events-auto' : 'pointer-events-none',
        TONE[item.tone],
      )}
    >
      <span>{item.message}</span>
      {item.persistent && (
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => onDismiss(item.id)}
          className="grid size-8 place-items-center rounded-control hover:bg-white/15"
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>.');
  return api;
}

/**
 * Floats toasts at the top of the viewport while the calling screen is mounted, and puts them back at the
 * bottom when it unmounts. For screens whose work sits at the bottom of long cards (the kitchen board),
 * where a bottom toast would cover the line it reports on.
 */
export function useToastPlacement(placement: ToastPlacement) {
  const { setPlacement } = useToast();
  useEffect(() => {
    setPlacement(placement);
    return () => setPlacement('bottom');
  }, [placement, setPlacement]);
}
